import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { subtleNeo } from '@brainsway/ui';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { useLocale } from '@/hooks/useLocale';
import { useMonthOptions } from '@/hooks/useMonthOptions';
import {
  useDashboardStats,
  useEarningsHistory,
  useTreatmentBreakdown,
  useWeeklyActivity,
  useMonthlySummary,
  useRecentReports,
} from '../hooks';
import {
  HeroStatsCard,
  EarningsChart,
  TreatmentBreakdownChart,
  WeeklyActivityChart,
  RecentReportsList,
  MonthlySummaryCard,
  QuickActions,
} from '../components';

const STATUS_OPTIONS = ['SUBMITTED', 'APPROVED', 'PAID'] as const;

export function DashboardPage() {
  const { t } = useTranslation();
  const locale = useLocale();

  // Month selector — undefined means current month
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['PAID']);

  const toggleStatus = useCallback((status: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        if (prev.length === 1) return prev;
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  }, []);

  const statusParam = useMemo(() => selectedStatuses.join(','), [selectedStatuses]);

  const { data: stats, isLoading: statsLoading } = useDashboardStats(selectedMonth, statusParam);
  const { data: earningsData } = useEarningsHistory(12, statusParam);
  const { data: breakdown } = useTreatmentBreakdown(selectedMonth, statusParam);
  const { data: weeklyData } = useWeeklyActivity(selectedMonth, statusParam);
  const { data: summary } = useMonthlySummary(selectedMonth, statusParam);
  const { data: recentReports } = useRecentReports(5);

  const monthOptions = useMonthOptions(locale);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <>
    <motion.div
      initial="hidden"
      animate="visible"
      variants={subtleNeo.pageContainer}
      className="space-y-6 px-4 sm:px-6 md:px-8"
    >
      {/* Header */}
      <motion.div variants={subtleNeo.staggerItem} className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">{t('dashboard.title')}</h1>
          <p className="text-xs sm:text-sm text-slate-500">{selectedMonth}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-xl p-1 glass-surface" style={{ border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            {STATUS_OPTIONS.map((s) => {
              const active = selectedStatuses.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-all ${
                    active
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-slate-500 hover:bg-white/40'
                  }`}
                >
                  {t(`status.${s.toLowerCase()}`)}
                </button>
              );
            })}
          </div>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="glass-surface rounded-xl px-3 py-1.5 text-sm text-slate-700 shadow-sm transition-all focus:bg-white/50 focus:shadow-sm focus:outline-none"
            style={{ border: '1px solid rgba(148, 163, 184, 0.3)' }}
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Quick Actions */}
      {stats && (
        <motion.div variants={subtleNeo.staggerItem}>
          <QuickActions pendingCount={stats.pendingReports} approvedCount={stats.approvedReports} />
        </motion.div>
      )}

      {/* Hero Stats */}
      {stats && (
        <motion.div variants={subtleNeo.staggerItem}>
          <HeroStatsCard stats={stats} />
        </motion.div>
      )}

      {/* Charts Row */}
      <ScrollReveal>
        <div className="grid gap-6 lg:grid-cols-2">
          {earningsData && <EarningsChart data={earningsData.history} reportTypes={earningsData.reportTypes} />}
          {breakdown && <TreatmentBreakdownChart data={breakdown} />}
        </div>
      </ScrollReveal>

      {/* Bottom Row */}
      <ScrollReveal delay={0.05}>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            {weeklyData && <WeeklyActivityChart data={weeklyData.activity} reportTypes={weeklyData.reportTypes} />}
          </div>
          <div className="lg:col-span-1">
            {summary && <MonthlySummaryCard summary={summary} />}
          </div>
          <div className="lg:col-span-1">
            {recentReports && <RecentReportsList reports={recentReports} />}
          </div>
        </div>
      </ScrollReveal>
    </motion.div>
    </>
  );
}
